import type { RateRow, RateStatus, TriggerEvent } from './types';

export interface RatesQuery {
  status: RateStatus[];
  projectType: string[];
  clientSearch: string;
  page: number;
  pageSize: number;
}

export interface RatesPage {
  rows: RateRow[];
  total: number;
  page: number;
  pageSize: number;
}

function buildQuery(q: RatesQuery): string {
  const p = new URLSearchParams();
  for (const s of q.status) p.append('status', s);
  for (const t of q.projectType) p.append('project_type', t);
  if (q.clientSearch.trim() !== '') p.set('client_search', q.clientSearch.trim());
  p.set('page', String(q.page));
  p.set('page_size', String(q.pageSize));
  return p.toString();
}

export async function fetchRates(q: RatesQuery): Promise<RatesPage> {
  const res = await fetch(`/api/admin/rates?${buildQuery(q)}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as RatesPage;
}

export async function patchRate(
  id: string,
  patch: {
    amount?: string;
    trigger_event?: TriggerEvent;
    status?: RateStatus;
    clickup_trigger_task_id?: string | null;
    notes?: string | null;
    email_custom_text?: string | null;
  },
): Promise<{ invoice_id: string | null }> {
  const res = await fetch(`/api/admin/rates/${id}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(json.error ?? `HTTP ${res.status}`);
  }
  const body = (await res.json().catch(() => ({}))) as { invoice_id?: string | null };
  return { invoice_id: body.invoice_id ?? null };
}

export async function createRate(
  projectId: string,
  body: { amount: string; trigger_event: TriggerEvent; notes?: string | null },
): Promise<void> {
  const res = await fetch(`/api/admin/projects/${projectId}/rates`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(json.error ?? `HTTP ${res.status}`);
  }
}

export interface TriggerNowResult {
  invoice_id: string | null;
  already_triggered: boolean;
}

export async function triggerRateNow(id: string): Promise<TriggerNowResult> {
  const res = await fetch(`/api/admin/rates/${id}/trigger-now`, { method: 'POST' });
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(json.error ?? `HTTP ${res.status}`);
  }
  const body = (await res.json()) as TriggerNowResult;
  return body;
}

export async function deleteRate(id: string): Promise<void> {
  const res = await fetch(`/api/admin/rates/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(json.error ?? `HTTP ${res.status}`);
  }
}
